import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { QuoteContext, type QuoteContextValue, type QuoteDraft } from '@/contexts/QuoteContext';
import { APP_CONFIG } from '@/config/app.config';
import { entitledPdfThemeId } from '@/config/pdfThemes';
import { useBusiness } from '@/hooks/useBusiness';
import { useDataStore } from '@/hooks/useDataStore';
import { useHistoryState } from '@/hooks/useHistoryState';
import { useSettings } from '@/hooks/useSettings';
import { useToast } from '@/hooks/useToast';
import { usePresets } from '@/hooks/usePresets';
import { useServices } from '@/hooks/useServices';
import { calculateTotals } from '@/services/pricing.service';
import {
  activePresetIds as computeActivePresetIds, addService as addServiceToItems, applyPreset as applyPresetToItems,
  catalogLineCount as computeCatalogLineCount, createBlankItem, findMatchingPreset,
  manualLineCount as computeManualLineCount, normaliseItems, presetContributionCounts,
  removePreset as removePresetFromItems, setItemQuantity,
} from '@/services/preset.service';
import { formatQuoteNumber } from '@/services/quoteNumber.service';
import type {
  BusinessProfile, Customer, ID, Quote, QuoteItem, QuotePreset, Service,
} from '@/types';
import { nowISO } from '@/utils/id';

const AUTOSAVE_DELAY_MS = 800;
const NO_QUOTES: Quote[] = [];

function emptyDraft(business: BusinessProfile | null): QuoteDraft {
  const validUntil = new Date();
  validUntil.setDate(validUntil.getDate() + APP_CONFIG.quoteValidityDays);

  return {
    customerId: null,
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    items: [],
    discount: { mode: 'percent', value: 0 },
    taxRate: business?.taxRate ?? 0,
    taxLabel: business?.taxLabel ?? 'Tax',
    deposit: { mode: 'percent', value: 0 },
    notes: '',
    terms: business?.terms ?? [],
    validUntil: validUntil.toISOString(),
  };
}

function isDraftWorthKeeping(draft: QuoteDraft): boolean {
  return draft.items.length > 0 || draft.customerName.trim().length > 0;
}

export function QuoteProvider({ children }: PropsWithChildren) {
  const store = useDataStore();
  const { activeBusiness, updateBusiness } = useBusiness();
  const { plan } = useSettings();
  // The provider owns the catalog and bundle lists so views can dispatch a
  // bundle by identity alone and never assemble line items themselves.
  const { services } = useServices();
  const { presets } = usePresets();
  const { notify } = useToast();

  const history = useHistoryState<QuoteDraft>(emptyDraft(null));

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isRestoredDraft, setIsRestoredDraft] = useState(false);
  /** The business `quotes` was loaded for, and the one the draft in history belongs to. */
  const [quotesFor, setQuotesFor] = useState<ID | null>(null);
  const [draftFor, setDraftFor] = useState<ID | null>(null);

  const businessId = activeBusiness?.id ?? null;
  /** Latest active business, read by the autosave cleanup to tell a switch from a keystroke. */
  const activeIdRef = useRef(businessId);
  activeIdRef.current = businessId;
  /** Blocks autosave until the stored draft has been read back. */
  const hydratedFor = useRef<string | null>(null);

  // Right after a business switch, history still holds the previous business's
  // draft until the new one is read back. It is never shown or used: until
  // then the draft is a blank one for the business that is active now.
  const blankDraft = useMemo(() => emptyDraft(activeBusiness), [activeBusiness]);
  const draft = draftFor === businessId ? history.state : blankDraft;
  /** Latest draft, so action creators stay referentially stable. */
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (!businessId) {
      setQuotes([]);
      setQuotesFor(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    void store.quotes.list().then((all) => {
      if (cancelled) return;
      setQuotes(
        all
          .filter((quote) => quote.businessId === businessId)
          .sort((a, b) => b.issuedAt.localeCompare(a.issuedAt)),
      );
      setQuotesFor(businessId);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [store, businessId]);

  // ── Autosave: restore ────────────────────────────────────────────
  useEffect(() => {
    if (!activeBusiness) return;
    if (hydratedFor.current === activeBusiness.id) return;

    let cancelled = false;
    void store.drafts.read().then((envelope) => {
      if (cancelled) return;
      const stored = envelope.drafts[activeBusiness.id];
      const restored = stored?.draft as QuoteDraft | undefined;

      if (restored && isDraftWorthKeeping(restored)) {
        history.reset({
          ...emptyDraft(activeBusiness),
          ...restored,
          items: normaliseItems(restored.items ?? []),
        });
        setLastSavedAt(stored?.savedAt ?? null);
        setIsRestoredDraft(true);
      } else {
        history.reset(emptyDraft(activeBusiness));
        // Nothing carried over from a business switched away from.
        setLastSavedAt(null);
        setIsRestoredDraft(false);
      }
      hydratedFor.current = activeBusiness.id;
      setDraftFor(activeBusiness.id);
    });

    return () => {
      cancelled = true;
    };
  }, [activeBusiness, store, history.reset]);

  // ── Autosave: persist ────────────────────────────────────────────
  useEffect(() => {
    if (!businessId || hydratedFor.current !== businessId) return;

    let written = false;
    /** `switching`: saved on the way out of this business, so its time is not shown for the next. */
    const persist = async (switching: boolean) => {
      written = true;
      try {
        const envelope = await store.drafts.read();
        const savedAt = nowISO();

        if (isDraftWorthKeeping(draft)) {
          await store.drafts.write({
            drafts: { ...envelope.drafts, [businessId]: { savedAt, draft } },
          });
          if (!switching) setLastSavedAt(savedAt);
        } else if (envelope.drafts[businessId]) {
          const { [businessId]: _discarded, ...rest } = envelope.drafts;
          await store.drafts.write({ drafts: rest });
          if (!switching) setLastSavedAt(null);
        }
      } catch (error) {
        // A full or unavailable device must not fail silently: the user is
        // relying on the draft being there when they come back.
        setLastSavedAt(null);
        notify(
          error instanceof Error ? error.message : 'Autosave failed on this device.',
          'error',
        );
      }
    };
    const timer = window.setTimeout(() => void persist(false), AUTOSAVE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      // Switching business while this business's last edits are still waiting
      // for the debounce: save them to this business now instead of dropping them.
      if (!written && activeIdRef.current !== businessId) void persist(true);
    };
  }, [draft, businessId, store, notify]);

  const totals = useMemo(
    () =>
      calculateTotals({
        items: draft.items,
        discount: draft.discount,
        taxRate: draft.taxRate,
        deposit: draft.deposit,
        currency: activeBusiness?.currency ?? 'USD',
      }),
    [draft.items, draft.discount, draft.taxRate, draft.deposit, activeBusiness?.currency],
  );

  const previewNumber = useMemo(
    () =>
      formatQuoteNumber(
        (activeBusiness?.quoteSequence ?? 0) + 1,
        activeBusiness?.quoteNumberPrefix,
      ),
    [activeBusiness?.quoteSequence, activeBusiness?.quoteNumberPrefix],
  );

  const patchDraft = useCallback<QuoteContextValue['patchDraft']>(
    (patch, options) => {
      setIsRestoredDraft(false);
      history.set((current) => ({ ...current, ...patch }), options);
    },
    [history.set],
  );

  const applyCustomer = useCallback(
    (customer: Customer | null) => {
      setIsRestoredDraft(false);
      history.set((current) => ({
        ...current,
        customerId: customer?.id ?? null,
        customerName: customer?.name ?? '',
        customerPhone: customer?.phone ?? '',
        customerEmail: customer?.email ?? '',
        customerAddress: customer?.address ?? '',
      }));
    },
    [history.set],
  );

  const addService = useCallback(
    (service: Service) => {
      setIsRestoredDraft(false);
      history.set((current) => ({ ...current, items: addServiceToItems(current.items, service) }));
    },
    [history.set],
  );

  const applyPreset = useCallback(
    (preset: QuotePreset) => {
      const result = applyPresetToItems(draftRef.current.items, preset, services);
      setIsRestoredDraft(false);
      history.set((current) => ({
        ...current,
        items: applyPresetToItems(current.items, preset, services).items,
      }));
      return { appliedCount: result.appliedCount, missingCount: result.missingCount };
    },
    [history.set, services],
  );

  const removePreset = useCallback(
    (preset: QuotePreset) => {
      const result = removePresetFromItems(draftRef.current.items, preset.id);
      setIsRestoredDraft(false);
      history.set((current) => ({
        ...current,
        items: removePresetFromItems(current.items, preset.id).items,
      }));
      return { removedCount: result.removedCount, keptCount: result.keptCount };
    },
    [history.set],
  );

  const addBlankItem = useCallback(() => {
    history.set((current) => ({ ...current, items: [...current.items, createBlankItem()] }));
  }, [history.set]);

  const updateItem = useCallback(
    (id: ID, patch: Partial<QuoteItem>) => {
      history.set(
        (current) => ({
          ...current,
          items:
            patch.quantity === undefined
              ? current.items.map((item) => (item.id === id ? { ...item, ...patch } : item))
              : setItemQuantity(
                  current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
                  id,
                  patch.quantity,
                ),
        }),
        { coalesce: true },
      );
    },
    [history.set],
  );

  const removeItem = useCallback(
    (id: ID) => {
      history.set((current) => ({
        ...current,
        items: current.items.filter((item) => item.id !== id),
      }));
    },
    [history.set],
  );

  const resetDraft = useCallback(() => {
    history.reset(emptyDraft(activeBusiness));
    setIsRestoredDraft(false);
  }, [history.reset, activeBusiness]);

  const saveQuote = useCallback(async () => {
    if (!activeBusiness) throw new Error('No active business — cannot issue a quote.');

    const sequence = activeBusiness.quoteSequence + 1;
    const number = formatQuoteNumber(sequence, activeBusiness.quoteNumberPrefix);

    const quote = await store.quotes.create({
      businessId: activeBusiness.id,
      number,
      status: 'draft',
      customerId: draft.customerId,
      customerSnapshot: {
        name: draft.customerName,
        phone: draft.customerPhone || undefined,
        email: draft.customerEmail || undefined,
        address: draft.customerAddress || undefined,
      },
      items: draft.items.map((item) => ({ ...item })),
      discount: { ...draft.discount },
      taxRate: draft.taxRate,
      taxLabel: draft.taxLabel,
      deposit: { ...draft.deposit },
      currency: activeBusiness.currency,
      notes: draft.notes || undefined,
      terms: [...draft.terms],
      // The theme this quote was drawn with, so reopening it never restyles it.
      themeId: entitledPdfThemeId(activeBusiness.pdfTheme, plan.capabilities),
      issuedAt: nowISO(),
      validUntil: draft.validUntil,
      totals,
    });

    await updateBusiness(activeBusiness.id, { quoteSequence: sequence });

    await Promise.all(
      draft.items
        .filter((item) => item.serviceId)
        .map(async (item) => {
          const service = await store.services.getById(item.serviceId as string);
          if (service) await store.services.update(service.id, { usageCount: service.usageCount + 1 });
        }),
    );

    if (draft.customerId) {
      const customer = await store.customers.getById(draft.customerId);
      if (customer) {
        await store.customers.update(customer.id, {
          quoteCount: customer.quoteCount + 1,
          lastQuotedAt: quote.issuedAt,
        });
      }
    }

    setQuotes((current) => [quote, ...current]);
    return quote;
  }, [activeBusiness, store, draft, totals, updateBusiness, plan]);

  const deleteQuote = useCallback(
    async (id: ID) => {
      await store.quotes.remove(id);
      setQuotes((current) => current.filter((quote) => quote.id !== id));
    },
    [store],
  );

  const loadQuoteIntoDraft = useCallback(
    (quote: Quote) => {
      history.reset({
        customerId: quote.customerId,
        customerName: quote.customerSnapshot.name,
        customerPhone: quote.customerSnapshot.phone ?? '',
        customerEmail: quote.customerSnapshot.email ?? '',
        customerAddress: quote.customerSnapshot.address ?? '',
        items: normaliseItems(quote.items).map((item, index) => ({
          ...item,
          id: `item_${Date.now()}_${index}`,
        })),
        discount: { ...quote.discount },
        taxRate: quote.taxRate,
        taxLabel: quote.taxLabel,
        deposit: { ...quote.deposit },
        notes: quote.notes ?? '',
        terms: [...quote.terms],
        validUntil: quote.validUntil,
      });
      setIsRestoredDraft(false);
    },
    [history.set],
  );

  const activePresetIdSet = useMemo(() => computeActivePresetIds(draft.items), [draft.items]);
  const presetCounts = useMemo(() => presetContributionCounts(draft.items), [draft.items]);
  const manualLines = useMemo(() => computeManualLineCount(draft.items), [draft.items]);
  const catalogLines = useMemo(() => computeCatalogLineCount(draft.items), [draft.items]);
  const matchedPreset = useMemo(
    () => findMatchingPreset(draft.items, presets),
    [draft.items, presets],
  );

  // A switched-away business's quotes are never exposed while the reload runs.
  const visibleQuotes = quotesFor === businessId ? quotes : NO_QUOTES;

  const value = useMemo<QuoteContextValue>(
    () => ({
      draft, totals, quotes: visibleQuotes, isLoading: isLoading || quotesFor !== businessId,
      previewNumber, patchDraft, applyCustomer, addService,
      applyPreset, removePreset, addBlankItem, updateItem, removeItem, resetDraft, saveQuote,
      deleteQuote, loadQuoteIntoDraft, undo: history.undo, redo: history.redo,
      canUndo: history.canUndo, canRedo: history.canRedo, lastSavedAt, isRestoredDraft,
      activePresetIds: activePresetIdSet, presetCounts, manualLineCount: manualLines,
      catalogLineCount: catalogLines, matchedPreset,
    }),
    [
      draft, totals, visibleQuotes, isLoading, quotesFor, businessId, previewNumber, patchDraft, applyCustomer, addService,
      applyPreset, removePreset, addBlankItem, updateItem, removeItem, resetDraft, saveQuote,
      deleteQuote, loadQuoteIntoDraft, history.undo, history.redo, history.canUndo, history.canRedo,
      lastSavedAt, isRestoredDraft, activePresetIdSet, presetCounts, manualLines, catalogLines,
      matchedPreset,
    ],
  );

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
}
